import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  isIDynamicBindableValue,
  Operation,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ComponentsRelationshipService } from '@epicuro-next/platform/dynamic-value-resolving';
import { isNonNullish } from '@epicuro-next/utilities';
import { of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

import { LogicOperationsService } from '../services/logic-operations.service';

type OperationType = Operation | null;
type OperationWithInstanceType = {
  instance: unknown;
  operation: OperationType;
};
type OperationInputType = OperationType | OperationWithInstanceType | null;

@Directive({
  selector: '[epcDynamicComponentIf]',
  standalone: false,
})
export class DynamicComponentIfDirective<T> implements OnChanges, OnDestroy {
  private operationCheck$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private viewRef: EmbeddedViewRef<T> | null = null;

  @Input('epcDynamicComponentIf')
  public operation: OperationInputType = null;

  constructor(
    templateRef: TemplateRef<T>,
    viewContainerRef: ViewContainerRef,
    changeDetectorRef: ChangeDetectorRef,
    logicalOperationsService: LogicOperationsService,
    componentsRelationshipService: ComponentsRelationshipService,
  ) {
    this.operationCheck$
      .pipe(
        switchMap(() => {
          const operationWithInstance = this.getOperation();

          if (
            isNonNullish(operationWithInstance) &&
            isNonNullish(operationWithInstance.operation)
          ) {
            let componentRef: ComponentRef<unknown> | undefined;
            if (operationWithInstance.instance) {
              componentRef = componentsRelationshipService.getComponentRef(
                operationWithInstance.instance,
              );

              if (componentRef === undefined) {
                throw new Error(
                  'Did not obtain a reference to a componentRef.',
                );
              }

              operationWithInstance.operation = JSON.parse(
                JSON.stringify(operationWithInstance.operation),
              );

              this.setParentProviderToSelfParentProvider(
                operationWithInstance.operation,
              );
            }

            return logicalOperationsService.performOperationCheck(
              operationWithInstance.operation,
              viewContainerRef.injector,
              componentRef,
            );
          }

          return of(true);
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((passed: boolean) => {
        if (!passed && this.viewRef) {
          viewContainerRef.clear();
          this.viewRef = null;
        } else if (passed && !this.viewRef) {
          this.viewRef = viewContainerRef.createEmbeddedView(templateRef);
        }

        changeDetectorRef.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.operation) {
      return;
    }

    this.operationCheck$.next();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getOperation(): {
    operation: Operation;
    instance?: unknown;
  } | null {
    const operation = this.operation;
    if (operation === null) {
      return null;
    }

    if (this.isOperationWithInstanceType(operation)) {
      if (operation.operation === null) {
        return null;
      }

      return {
        operation: operation.operation,
        instance: operation.instance,
      };
    }

    return { operation, instance: undefined };
  }

  private isOperationWithInstanceType(
    operation: OperationType | OperationWithInstanceType,
  ): operation is OperationWithInstanceType {
    if (operation === null) {
      return false;
    }

    const key: keyof OperationWithInstanceType = 'instance';
    if (typeof operation === 'object' && key in operation) {
      return true;
    }

    return false;
  }

  /**
   * This methods updates the IDynamicBindable value for relative providers (self and parent).
   * An 'if' can never be put on 'self', as that instance does not exist yet, because the 'if' has yet to be evaluated.
   * Looking at the parent works, but there is an intermediary parent added by the dynamic framework itself when rendering children.
   * If this would ever be removed, the 'parent' is not the parent anymore, but the 'self'.
   * So therefore start with the 'self' and move on to the 'parent'.
   */
  private setParentProviderToSelfParentProvider(value: unknown): void {
    if (Array.isArray(value)) {
      for (const v of value) {
        this.setParentProviderToSelfParentProvider(v);
      }
    } else if (isIDynamicBindableValue(value)) {
      // tslint:disable-next-line:no-collapsible-if
      if (value.provider === 'parent' || value.provider === 'self') {
        value.provider = 'self-parent';
      }
    }
  }
}
